<script setup lang="ts">
import ElListItem, { ElListItemProps } from '@/ElListItem.vue';
import ElInputText from '@/forms/ElInputText.vue';
import ElIconButton from '@/ElIconButton.vue';
import ElButton, { ElButtonProps } from '@/ElButton.vue';
import { computed, ref, watch } from 'vue';
import ElTag, { ElTagColor } from '@/ElTag.vue';
import ElModal from '@/ElModal.vue';
import ElInputSelect from '@/forms/ElInputSelect.vue';
import ElInputDate from '@/forms/ElInputDate.vue';
import ElAvatar from '@/ElAvatar.vue';
import CustomTransition from '@/_CustomTransition.vue';
import ElSpinner from '@/ElSpinner.vue';
import ElIcon from '@/ElIcon.vue';
import { OptionType } from '@/types';
import { compareSortableValues } from '@/table/ElTableCell.vue';

export type SelectOptions = OptionType[];

export type MobileTableFiltersStatus = {
  freeSearch?: string;
  fromDate?: number;
  multiSelect: {
    mainText?: string;
    secondaryRow?: string;
    tertiaryRow?: string;
    tags?: string;
    time?: string;
    amount?: string;
    avatar?: string;
  };
};

export type MobileTableFilter = {
  freeSearch?: {
    placeholder?: string;
    filterOn: ('mainText' | 'secondaryRow' | 'tertiaryRow' | 'amount' | 'tags' | 'time' | 'avatar')[];
    initialValue?: string;
  };
  multiValue?: {
    mainText?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
    secondaryRow?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
    tertiaryRow?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
    amount?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
    tags?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
    time?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
    avatar?: {
      enabled: boolean;
      label: string;
      selectOptions?: SelectOptions;
      initialValue?: string;
    };
  };
  dateRange?: {
    enabled: boolean;
    label: string;
    initialValue?: number;
    placeholder?: string;
  };
  resetButton?: boolean;
  // if true filters won't be applied by the table (used for server side table)
  managed?: boolean;
};

export type MobileTableRow = Omit<ElListItemProps, 'color'> & {
  id: string;
  rowNotSelectable?: boolean;
  relatedObject?: any;
  clickAction?: (payload: PointerEvent) => void;
  actions?: {
    primary: InstanceType<typeof ElButton>['$props'];
    secondary?: InstanceType<typeof ElButton>['$props'];
  };
  sortableAndFilterableValue?: string;
  details: {
    label: string;
    value:
      | { type?: 'default'; text: string }
      | { type: 'tag'; text: string; color?: ElTagColor }
      | { type: 'avatar'; label?: string; picture?: string };
  }[];
};

export interface ElMobileTableProps {
  color: 'primary' | 'secondary';
  filters: MobileTableFilter;
  loading?: boolean;
  preventDefaultItemClick?: boolean;
  selection?: {
    enabled: boolean;
    selectionMode?: 'single' | 'multiple'; // defaults to multiple
    destructiveAction?: {
      enabled: boolean;
      button: Omit<ElButtonProps, 'variant' | 'type' | 'label' | 'error' | 'onClick'> & { label?: (count: number) => string };
    };
    bulkActionButton: Omit<ElButtonProps, 'variant' | 'type' | 'label' | 'error' | 'onClick'> & { label: (count: number) => string };
    selectedLabel?: (selectionCount: number) => string;
    cancelText?: (selectionCount: number) => string;
  };
  sortBy?: {
    on: 'mainText' | 'secondaryRow' | 'tertiaryRow' | 'amount' | 'tags' | 'time' | 'avatar';
    order: 'asc' | 'desc';
  };
  itemsCountLabel?: (count: number) => string;
  noItemsCountLabel?: boolean;
  data: MobileTableRow[];
}

const props = defineProps<ElMobileTableProps>();

const search = ref<string>('');
const selectedItems = ref<number[]>([]);
const selectedItemToShow = ref<MobileTableRow | null>(null);
const filterModalOpen = ref(false);
const filterDateFrom = ref<number>(0);
const managedSearchDebounceTimeout = ref<ReturnType<typeof setTimeout> | undefined>(undefined);

watch(search, (newValue, oldValue) => {
  if (managedSearchDebounceTimeout.value) {
    clearTimeout(managedSearchDebounceTimeout.value);
  }
  managedSearchDebounceTimeout.value = setTimeout(() => {
    if (oldValue !== newValue) {
      emitFiltersUpdated();
    }
  }, 500);
});

const filtersAreApplied = computed(() => {
  return (
    search.value.trim().length > 0 ||
    Object.values(multiValueFiltersValues.value).some(filter => filter && filter.trim().length > 0) ||
    filterDateFrom.value !== 0
  );
});

const getSelectedRows = () => selectedItems.value.map(index => props.data[index]);

const matchesSearch = (value: string): boolean => {
  const searchText = search.value?.trim();
  if (!searchText) return true;
  return value.split(' ').some(part => part.toLowerCase().includes(searchText.toLowerCase()));
};

const multiValueFiltersValues = ref<MobileTableFiltersStatus['multiSelect']>({
  avatar: props.filters.multiValue?.avatar?.initialValue ?? '',
  mainText: props.filters.multiValue?.mainText?.initialValue ?? '',
  secondaryRow: props.filters.multiValue?.secondaryRow?.initialValue ?? '',
  tertiaryRow: props.filters.multiValue?.tertiaryRow?.initialValue ?? '',
  amount: props.filters.multiValue?.amount?.initialValue ?? '',
  tags: props.filters.multiValue?.tags?.initialValue ?? '',
  time: props.filters.multiValue?.time?.initialValue ?? '',
});

const multiValueFiltersModalValues = ref<MobileTableFiltersStatus['multiSelect']>({
  ...multiValueFiltersValues.value,
});

const makeArrUnique = (arr: string[]): string[] => Array.from(new Set(arr));
const isDefined = <T,>(value: T | undefined): value is T => value !== undefined;

const multiValueAvailabeOptions = computed<{
  mainText: SelectOptions;
  secondaryRow: SelectOptions;
  tertiaryRow: SelectOptions;
  amount: SelectOptions;
  tags: SelectOptions;
  time: SelectOptions;
  avatar: SelectOptions;
}>(() => {
  return {
    mainText: props.filters.multiValue?.mainText
      ? props.filters.multiValue.mainText.selectOptions ??
        makeArrUnique(props.data.map(item => item.mainText.label)).map(value => ({ value, label: value }))
      : [],
    secondaryRow: props.filters.multiValue?.secondaryRow
      ? props.filters.multiValue.secondaryRow.selectOptions ??
        makeArrUnique(props.data.map(item => item.secondaryRow).filter(isDefined)).map(value => ({ value, label: value }))
      : [],
    tertiaryRow: props.filters.multiValue?.tertiaryRow
      ? props.filters.multiValue.tertiaryRow.selectOptions ??
        makeArrUnique(props.data.map(item => item.tertiaryRow).filter(isDefined)).map(value => ({ value, label: value }))
      : [],
    amount: props.filters.multiValue?.amount
      ? props.filters.multiValue.amount.selectOptions ??
        makeArrUnique(props.data.map(item => item.amount).filter(isDefined)).map(value => ({ value, label: value }))
      : [],
    tags: props.filters.multiValue?.tags
      ? props.filters.multiValue.tags.selectOptions ??
        makeArrUnique(props.data.flatMap(item => (item.tags ?? []).map(tag => tag.text).filter(isDefined))).map(value => ({
          value,
          label: value,
        }))
      : [],
    time: props.filters.multiValue?.time
      ? props.filters.multiValue.time.selectOptions ??
        makeArrUnique(props.data.map(item => item.time).filter(isDefined)).map(value => ({ value, label: value }))
      : [],
    avatar: props.filters.multiValue?.avatar
      ? props.filters.multiValue.avatar.selectOptions ??
        makeArrUnique(props.data.map(item => item.avatar?.label).filter(isDefined)).map(value => ({ value, label: value }))
      : [],
  };
});

const filteredData = computed(() => {
  let data = props.data.slice();
  if (props.filters.managed) return data;
  if (props.filters.freeSearch && search.value.trim().length > 0) {
    const searchParams = props.filters.freeSearch;
    data = data.filter(item =>
      searchParams.filterOn.some(filter => {
        switch (filter) {
          case 'avatar':
            return item.avatar && item.avatar.label && matchesSearch(item.avatar.label);
          case 'time':
            return item.time && matchesSearch(item.time);
          case 'mainText':
            return item.mainText && matchesSearch(item.mainText.label);
          case 'secondaryRow':
            return item.secondaryRow && matchesSearch(item.secondaryRow);
          case 'tertiaryRow':
            return item.tertiaryRow && matchesSearch(item.tertiaryRow);
          case 'amount':
            return item.amount && matchesSearch(item.amount);
          case 'tags':
            return item.tags && item.tags.some(tag => matchesSearch(tag.text));
        }
      }),
    );
  }

  if (props.filters.multiValue) {
    if (props.filters.multiValue.mainText) {
      data = data.filter(
        item =>
          !multiValueFiltersValues.value.mainText ||
          multiValueFiltersValues.value.mainText.length === 0 ||
          multiValueFiltersValues.value.mainText.includes(item.sortableAndFilterableValue ?? item.mainText.label),
      );
    }
    if (props.filters.multiValue.secondaryRow) {
      data = data.filter(
        item =>
          !multiValueFiltersValues.value.secondaryRow ||
          multiValueFiltersValues.value.secondaryRow.length === 0 ||
          multiValueFiltersValues.value.secondaryRow.includes(item.sortableAndFilterableValue ?? item.secondaryRow ?? ''),
      );
    }
    if (props.filters.multiValue.tertiaryRow) {
      data = data.filter(
        item =>
          !multiValueFiltersValues.value.tertiaryRow ||
          multiValueFiltersValues.value.tertiaryRow.length === 0 ||
          multiValueFiltersValues.value.tertiaryRow.includes(item.sortableAndFilterableValue ?? item.tertiaryRow ?? ''),
      );
    }
    if (props.filters.multiValue.amount) {
      data = data.filter(
        item =>
          !multiValueFiltersValues.value.amount ||
          multiValueFiltersValues.value.amount.length === 0 ||
          multiValueFiltersValues.value.amount.includes(item.sortableAndFilterableValue ?? item.amount ?? ''),
      );
    }
    if (props.filters.multiValue.tags) {
      data = data.filter(
        item =>
          !multiValueFiltersValues.value.tags ||
          multiValueFiltersValues.value.tags.length === 0 ||
          item.tags?.some(tag => multiValueFiltersValues.value.tags!.includes(tag.text)),
      );
    }
    if (props.filters.multiValue.avatar) {
      data = data.filter(
        item =>
          !multiValueFiltersValues.value.avatar ||
          multiValueFiltersValues.value.avatar.length === 0 ||
          multiValueFiltersValues.value.avatar.includes(item.sortableAndFilterableValue ?? item.avatar?.label ?? ''),
      );
    }
  }

  if (props.filters.dateRange?.enabled) {
    data = data.filter(item => {
      if (!filterDateFrom.value) return true;
      return item.time && new Date(item.time).getTime() >= filterDateFrom.value;
    });
  }

  if (props.sortBy) {
    const desc = props.sortBy.order === 'desc';

    const getSortValue = (item: MobileTableRow): string => {
      if (item.sortableAndFilterableValue) return String(item.sortableAndFilterableValue);

      switch (props.sortBy!.on) {
        case 'mainText':
          return String(item.mainText.label);
        case 'avatar':
          return String(item.avatar?.label ?? '');
        case 'time':
        case 'secondaryRow':
        case 'tertiaryRow':
          return String(item[props.sortBy!.on] ?? '');
        case 'amount':
          return item.amount ?? '';
        case 'tags':
          return item.tags?.map(tag => tag.text).join('') ?? '';
      }
    };

    data = data.sort((a, b) => {
      const sortValueA = getSortValue(a);
      const sortValueB = getSortValue(b);
      return desc ? compareSortableValues(sortValueB, sortValueA) : compareSortableValues(sortValueA, sortValueB);
    });
  }

  return data;
});

const enabledSelectFilters = computed(() => {
  return Object.fromEntries(Object.entries(props.filters.multiValue ?? {}).filter(([_, filter]) => filter.enabled));
});

const showFiltersButton = computed(() => {
  return props.filters.dateRange?.enabled || Object.values(props.filters.multiValue ?? {}).some(filter => filter.enabled);
});

const hasFiltersApplied = computed(() => {
  return Object.values(multiValueFiltersValues.value).some(filter => filter.length > 0) || filterDateFrom.value !== 0;
});

const selectRows = (rowsIds: string[]) => {
  const indexes = rowsIds.map(id => getItemIndex({ id: id }));
  if (props.selection?.selectionMode === 'single' && indexes.length > 0) {
    selectedItems.value = [indexes.at(0)!];
  }
  selectedItems.value.push(...indexes);
  selectedItems.value = Array.from(new Set(selectedItems.value));
};

const selectRow = (rowIndex: number) => {
  if (props.selection?.selectionMode === 'single') {
    selectedItems.value = [rowIndex];
  } else {
    selectedItems.value.push(rowIndex);
    selectedItems.value = Array.from(new Set(selectedItems.value));
  }
  emit('rows-selected', getSelectedRows());
};

const deselectRow = (rowIndex: number) => {
  selectedItems.value = selectedItems.value.filter(index => index !== rowIndex);
};

const getItemIndex = (item: { id: string }) => {
  const idx = props.data.findIndex(i => i.id === item.id);
  if (idx === -1) throw new Error(`Item with id ${item.id} not found`);
  return idx;
};

const unselectAllRows = () => {
  selectedItems.value = [];
};

const getDataRows = () => props.data;

const openFiltersModal = () => {
  filterModalOpen.value = true;
};

const showItemDetails = (item: MobileTableRow) => {
  selectedItemToShow.value = item;
};

const closeItemDetails = () => {
  selectedItemToShow.value = null;
};

const detailsPrimaryAction = computed<Omit<InstanceType<typeof ElButton>['$props'], 'error' | 'size' | 'variant'>>(() => {
  if (selectedItemToShow.value?.actions) {
    return selectedItemToShow.value.actions.primary;
  } else {
    return {
      label: 'Chiudi',
      onClick: closeItemDetails,
    };
  }
});
const detailsSecondaryAction = computed<Omit<InstanceType<typeof ElButton>['$props'], 'error' | 'size' | 'variant'> | undefined>(() => {
  if (selectedItemToShow.value?.actions?.secondary) {
    return selectedItemToShow.value.actions.secondary;
  } else if (selectedItemToShow.value?.actions?.primary) {
    return {
      label: 'Chiudi',
      onClick: closeItemDetails,
    };
  } else {
    return undefined;
  }
});
const detailsTertiaryAction = computed<Omit<InstanceType<typeof ElButton>['$props'], 'error' | 'size' | 'variant'> | undefined>(() => {
  if (selectedItemToShow.value?.actions?.secondary) {
    return {
      label: 'Chiudi',
      onClick: closeItemDetails,
    };
  } else {
    return undefined;
  }
});

const applyFilters = () => {
  filterModalOpen.value = false;
  multiValueFiltersValues.value = { ...multiValueFiltersModalValues.value };
  emitFiltersUpdated();
};

const resetFilters = () => {
  multiValueFiltersValues.value = {
    avatar: '',
    time: '',
    mainText: '',
    secondaryRow: '',
    tertiaryRow: '',
    amount: '',
    tags: '',
  };
  filterDateFrom.value = 0;
  multiValueFiltersModalValues.value = {
    avatar: '',
    time: '',
    mainText: '',
    secondaryRow: '',
    tertiaryRow: '',
    amount: '',
    tags: '',
  };
  emitFiltersUpdated();
};

const emitFiltersUpdated = () => {
  if (props.filters.managed) {
    emit('filters-updated', {
      freeSearch: props.filters.freeSearch ? search.value : undefined,
      fromDate: props.filters.dateRange ? filterDateFrom.value : undefined,
      multiSelect: multiValueFiltersValues.value,
    });
  }
};

const emit = defineEmits<{
  (event: 'destructive:click', selectedItems: MobileTableRow[]): void;
  (event: 'bulk:click', selectedItems: MobileTableRow[]): void;
  (event: 'item:click', item: MobileTableRow): void;
  (event: 'selection:exit'): void;
  (event: 'rows-selected', selectedItems: MobileTableRow[]): void;
  (event: 'filters-updated', filters: MobileTableFiltersStatus): void;
}>();

defineExpose({
  filteredData,
  selectRows,
  unselectAllRows,
  getDataRows,
  getSelectedRows,
  closeItemDetails,
});
</script>

<template>
  <div class="bg-neutral-surface flex flex-col px-4 pt-2 h-full min-w-80 overflow-auto">
    <div v-if="!props.selection?.enabled" class="flex flex-row items-center gap-4 pb-2">
      <div class="flex-grow">
        <ElInputText
          v-if="props.filters.freeSearch"
          v-model="search"
          :placeholder="props.filters.freeSearch.placeholder ?? 'Cerca'"
          :leading-icon="{ name: 'MagnifyingGlassIcon' }"
        />
      </div>
      <ElIconButton
        v-if="showFiltersButton"
        :badge="hasFiltersApplied"
        :badge-color="props.color"
        :icon="{ name: 'AdjustmentsVerticalIcon' }"
        :class="
          props.color === 'primary'
            ? 'text-primary hover:text-primary-hover hover:bg-primary-light-hover active:bg-primary-light-active'
            : 'text-secondary hover:text-secondary-hover hover:bg-secondary-light-hover active:bg-secondary-light-active'
        "
        @click="openFiltersModal"
      />
    </div>

    <div v-if="props.selection?.enabled" class="py-3 px-4 border-b border-b-neutral-surface grid grid-cols-3">
      <div class="flex flex-row justify-start items-center">
        <ElButton
          error
          variant="tertiary"
          type="button"
          :label="`Elimina (${selectedItems.length})`"
          @click="$emit('destructive:click', getSelectedRows())"
        />
      </div>
      <div class="flex flex-row justify-center items-center">
        <span class="text-neutral-darker font-normal text-sm">
          <template v-if="props.selection.selectedLabel">
            {{ props.selection.selectedLabel(selectedItems.length) }}
          </template>
          <template v-else-if="props.selection.selectionMode !== 'single'">{{ selectedItems.length }} Selezionati</template>
        </span>
      </div>
    </div>
    <slot />

    <CustomTransition name="fade">
      <div class="relative">
        <ElSpinner
          v-if="props.loading"
          label="Caricamento dati..."
          class="p-4 absolute bg-neutral-surface w-full"
          :extra-loading-msg-after-seconds="3"
        />
      </div>
    </CustomTransition>

    <div v-if="filtersAreApplied && noItemsCountLabel !== true && filteredData.length !== 0" class="pt-3 px-4">
      <span class="font-semibold text-neutral-darker">{{
        props.itemsCountLabel ? props.itemsCountLabel(filteredData.length) : `${filteredData.length} risultati per la tua ricerca`
      }}</span>
    </div>

    <ul v-if="filteredData.length > 0" class="m-0 flex flex-col flex-grow overflow-y-auto h-full">
      <ElListItem
        v-for="item in filteredData"
        v-bind="item"
        :key="item.id"
        :color="props.color"
        :model-value="props.selection?.enabled ? selectedItems.includes(getItemIndex(item)) : undefined"
        @update:model-value="v => (v ? selectRow(getItemIndex(item)) : deselectRow(getItemIndex(item)))"
        @click="
          () => {
            $emit('item:click', item);
            if (!props.selection?.enabled && !props.preventDefaultItemClick) {
              showItemDetails(item);
            }
          }
        "
      />
      <slot name="after-list" />
    </ul>

    <div v-else-if="!props.loading && filteredData.length === 0" class="flex flex-grow flex-col justify-start items-center pt-8 px-8 gap-3">
      <ElIcon
        name="MagnifyingGlassIcon"
        class="h-8 w-8"
        :class="props.color === 'primary' ? 'text-primary-active' : 'text-secondary-active'"
      />
      <div class="flex flex-col gap-1 text-center">
        <span class="text-lg font-semibold text-neutral-darker">Nessun risultato</span>
        <p class="text-neutral-lighter text-sm">Non abbiamo trovato risultati per questa ricerca. Prova con altre parole chiave.</p>
      </div>
    </div>

    <div v-if="props.selection?.enabled" class="border-t border-t-neutral-surface flex flex-row justify-between py-5 px-4">
      <ElButton size="l" variant="tertiary" label="Annulla" @click="$emit('selection:exit')" />
      <ElButton
        v-if="props.selection?.bulkActionButton"
        :variant="props.color"
        :label="props.selection.bulkActionButton.label(selectedItems.length)"
        :disabled="selectedItems.length === 0"
        size="l"
        @click="
          $emit(
            'bulk:click',
            selectedItems.map(index => props.data[index]),
          )
        "
      />
    </div>
  </div>

  <ElModal
    size="xs"
    :is-open="selectedItemToShow !== null"
    :header-icon="{ name: 'AdjustmentsVerticalIcon' }"
    title="Dettaglio"
    :color="props.color"
    :primary-action="detailsPrimaryAction"
    :secondary-action="detailsSecondaryAction"
    :tertiary-action="detailsTertiaryAction"
    @closed="closeItemDetails"
  >
    <ul v-if="selectedItemToShow" class="m-0 flex flex-col gap-5">
      <li v-for="(item, i) in selectedItemToShow.details" :key="i" class="flex flex-col gap-1">
        <span class="text-neutral-lighter">{{ item.label }}</span>
        <span v-if="item.value.type === 'default'">{{ item.value.text }}</span>
        <div v-else-if="item.value.type === 'tag'">
          <ElTag :text="item.value.text" :color="item.value.color" />
        </div>
        <div v-else-if="item.value.type === 'avatar'">
          <ElAvatar :label="item.value.label" :picture="item.value.picture" size="sm" />
        </div>
      </li>
    </ul>
  </ElModal>

  <ElModal
    v-model:is-open="filterModalOpen"
    size="xs"
    :header-icon="{ name: 'AdjustmentsVerticalIcon' }"
    title="Filtri"
    :color="props.color"
    :primary-action="{
      label: 'Applica filtri',
      onClick: applyFilters,
    }"
    :secondary-action="props.filters.resetButton ? { label: 'Reset', onClick: resetFilters } : undefined"
    :tertiary-action="{
      label: 'Chiudi',
      onClick: () => {
        filterModalOpen = false;
      },
    }"
  >
    <div class="flex flex-col gap-6">
      <ElInputSelect
        v-for="(filter, key) in enabledSelectFilters"
        :key="key"
        v-model="multiValueFiltersModalValues[key as keyof typeof multiValueFiltersModalValues]"
        :options="multiValueAvailabeOptions[key as keyof typeof multiValueAvailabeOptions]"
        :label="filter.label"
      />
      <ElInputDate
        v-if="props.filters.dateRange?.enabled"
        v-model="filterDateFrom"
        :label="props.filters.dateRange.label"
        :placeholder="props.filters.dateRange.placeholder"
        hidden-error-message
      />
    </div>
  </ElModal>
</template>
